import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import {
    Col,
    OverlayTrigger,
    Row,
    ThemeProvider,
    Tooltip,
} from "react-bootstrap";
import uuid from "react-uuid";

import {
    axiosGetRequest,
    axiosPostRequest,
    axiosPutRequest,
} from "../../api/ApiClient";
import Dialog from "../../components/Dialog";
import ButtonStyles from "../../styles/components/ButtonStyles";
import TableStyles from "../../styles/components/TableStyles";
import { ReactComponent as LeftArrow } from "../../assets/icons/LeftArrow.svg";
import { ReactComponent as RightArrow } from "../../assets/icons/RightArrow.svg";
import { ReactComponent as BlockIcon } from "../../assets/icons/Block.svg";
import { ReactComponent as ActiveIcon } from "../../assets/icons/Active.svg";
import { ReactComponent as SuccessModal } from "../../assets/icons/SuccessModal.svg";
import { ReactComponent as PenToSquareRegularIcon } from "../../assets/icons/PenToSquareRegular.svg";
import {
    StyledBody,
    StyledHeader,
    StyledTitle,
    StyledSubTitle,
} from "../../styles/MainStyles";
import GenericUtils from "../../utils/GenericUtils";
import StringUtils from "../../utils/types/StringUtils";
import UploadFileStyles from "../../styles/pages/UploadFilesStyles";
import DialogCreateAbono from "./DialogCreateAbono";
import UserManagementStyle from "../../styles/pages/UserManagement";
import DialogEditAbono from "./DialogEditAbono";
import DialogMessage from "../../components/DialogMessage";

// eslint-disable-next-line sonarjs/cognitive-complexity
function gestaoAbono() {
    const { StyledImportButton, StyledModalTitle } = UploadFileStyles;
    const { StyledLabel } = UserManagementStyle;
    const { DefaultButtonLink } = ButtonStyles;
    const [itensPerPage] = useState(10);
    const [abono, setAbono] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [abonoIdValue, setAbonoIdValue] = useState(0);
    const [activeModal, setActiveModal] = useState(false);
    const [step, setSpet] = useState("ativar");
    const [confirmInactiveModal, setConfirmInactiveModal] = useState(false);
    const [modalCreate, setModalCreate] = useState(false);
    const [modalEdit, setModalEdit] = useState(false);
    const [objectAbono, setObjectAbono] = useState<any>({});
    const [errorTitle, setErrorTitle] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { instance } = useMsal();
    const theme = {};

    function clearPagination(response?: any) {
        setTotalRecords(response?.data?.length || 0);
        setCurrentPage(0);
    }

    function updatePagination(response: any, offset: number) {
        const tempCurrentPage = offset;
        const tempTotalRecords = response.headers["content-range"].split("/")[1]
            ? Number(response.headers["content-range"].split("/")[1])
            : 0;
        setCurrentPage(tempCurrentPage);
        setTotalRecords(tempTotalRecords);
    }

    function showError(title: string, message: string) {
        setErrorTitle(title);
        setErrorMessage(message);
    }

    async function getAbono() {
        const response = await axiosGetRequest(
            `docente/v1/tipo-abono?_limit=10&_offset=${currentPage}`,
            GenericUtils.getAccessTokenDocente(),
            instance
        );

        if (response?.data?._result) {
            setAbono(response.data._result);
            updatePagination(response, Number(currentPage) || 0);
        } else if (response.status === 200 && response.data) {
            setAbono(response.data);
            clearPagination(response);
        } else {
            setAbono([]);
            clearPagination();
        }
    }

    useEffect(() => {
        getAbono();
    }, [currentPage]);

    const handleEdit = async (abonoId: number) => {
        const response = await axiosGetRequest(
            `docente/v1/tipo-abono/${abonoId}`,
            GenericUtils.getAccessTokenDocente(),
            instance
        );
        setAbonoIdValue(abonoId);
        setObjectAbono(response.data);
        setModalEdit(true);
    };

    const handleActive = (abonoId: number) => {
        setAbonoIdValue(abonoId);
        setSpet("ativar");
        setActiveModal(true);
    };

    const handleInactive = (abonoId: number) => {
        setAbonoIdValue(abonoId);
        setSpet("inativar");
        setActiveModal(true);
    };

    const handleClose = () => {
        setActiveModal(false);
        setModalCreate(false);
        setModalEdit(false);
        setConfirmInactiveModal(false);
    };

    const isValidReason = (
        motivo: string,
        abonoId: number | null = null
    ): boolean => {
        if (!abono.length) return false;

        if (abonoId)
            return abono.find(
                (row) => row.motivo === motivo && row.tipoAbonoID !== abonoId
            );

        return abono.find((row) => row.motivo === motivo);
    };

    const handleSubmitCreate = async (
        motivo: string,
        quantidadeDias: number
    ) => {
        if (isValidReason(motivo.trim())) {
            showError("Motivo inválido", "Motivo informado já existe na lista");
            return;
        }

        const response = await axiosPostRequest(
            `docente/v1/tipo-abono`,
            {
                motivo,
                ativo: true,
                quantidadeDias,
            },
            GenericUtils.getAccessTokenDocente(),
            instance
        );

        if (response?.data?._result) {
            getAbono();
        } else if (response.status === 201) {
            getAbono();
            setModalCreate(false);
            clearPagination(response);
            setConfirmInactiveModal(true);
        } else if (response.status === 400 || response.status === 500) {
            showError(
                "Erro ao salvar",
                response?.data?.details || "Erro desconhecido!"
            );
        } else {
            clearPagination();
        }
    };

    const handleSubmitEdit = async (motivo: string, quantidadeDias: number) => {
        if (isValidReason(motivo.trim(), abonoIdValue)) {
            showError("Motivo inválido", "Motivo informado já existe na lista");
            return;
        }

        const response = await axiosPutRequest(
            `docente/v1/tipo-abono/${abonoIdValue}`,
            {
                motivo,
                quantidadeDias,
            },
            GenericUtils.getAccessTokenDocente(),
            instance
        );

        if (response?.data?._result) {
            setModalEdit(false);
            getAbono();
        } else if (response.status === 204) {
            setConfirmInactiveModal(true);
            setModalEdit(false);
            getAbono();
            setModalCreate(false);
            clearPagination(response);
        } else if (response.status === 400 || response.status === 500) {
            showError(
                "Erro ao salvar",
                response?.data?.details || "Erro desconhecido!"
            );
        } else {
            clearPagination();
        }
    };

    const actionsTable = (ativo: boolean, abonoId: number) => {
        const renderTooltipActive = (props: any) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tooltip {...props}>Ativar</Tooltip>
        );

        const renderTooltipInactivate = (props: any) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tooltip {...props}>Inativar</Tooltip>
        );

        const renderTooltipProfile = (props: any) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tooltip {...props}>Editar</Tooltip>
        );

        return (
            <div className="d-grid justify-content-center">
                <Row>
                    <Col xs="4">
                        <OverlayTrigger
                            placement="auto"
                            overlay={renderTooltipProfile}
                        >
                            <ButtonStyles.BtnTable
                                onClick={() => handleEdit(abonoId)}
                                variant="link"
                            >
                                <PenToSquareRegularIcon
                                    style={{
                                        width: 20,
                                        fill: "#404040",
                                    }}
                                />
                            </ButtonStyles.BtnTable>
                        </OverlayTrigger>
                    </Col>
                    <Col xs="4">
                        <OverlayTrigger
                            placement="auto"
                            overlay={renderTooltipActive}
                        >
                            <ButtonStyles.BtnTable
                                onClick={() => handleActive(abonoId)}
                                variant="link"
                                disabled={ativo}
                            >
                                <ActiveIcon
                                    style={{
                                        fill: !ativo ? "#404040" : "#E4E4E4",
                                    }}
                                />
                            </ButtonStyles.BtnTable>
                        </OverlayTrigger>
                    </Col>
                    <Col xs="4">
                        <OverlayTrigger
                            placement="auto"
                            overlay={renderTooltipInactivate}
                        >
                            <ButtonStyles.BtnTable
                                onClick={() => handleInactive(abonoId)}
                                variant="link"
                                disabled={!ativo}
                            >
                                <BlockIcon
                                    style={{
                                        fill: ativo ? "#404040" : "#E4E4E4",
                                    }}
                                />
                            </ButtonStyles.BtnTable>
                        </OverlayTrigger>
                    </Col>
                </Row>
            </div>
        );
    };

    const handleCloseInactiveConfirm = async () => {
        if (abonoIdValue) {
            await axiosPutRequest(
                `docente/v1/tipo-abono/${abonoIdValue}`,
                {
                    ativo: step === "ativar" ? "true" : "false",
                },
                GenericUtils.getAccessTokenDocente(),
                instance
            );
        }
        setActiveModal(false);
        await getAbono();
        setConfirmInactiveModal(true);
    };

    const DialogActiveConfirm = (
        <Dialog openModal={activeModal} onHide={handleClose}>
            <StyledModalTitle>
                Deseja {step} o abono selecionado?
            </StyledModalTitle>

            <Row style={{ justifyContent: "center" }}>
                <StyledImportButton
                    style={{ width: 126, marginTop: 24 }}
                    onClick={handleCloseInactiveConfirm}
                >
                    {step[0].toUpperCase() + step.slice(1)}
                </StyledImportButton>
            </Row>
            <div style={{ height: 15 }} />
            <Row style={{ justifyContent: "center" }}>
                <DefaultButtonLink
                    onClick={() => {
                        handleClose();
                    }}
                >
                    Cancelar
                </DefaultButtonLink>
            </Row>
        </Dialog>
    );

    const DialogConfirm = (
        <Dialog
            openModal={confirmInactiveModal}
            onHide={handleClose}
            modalIcon={<SuccessModal />}
        >
            <StyledModalTitle>Operação realizada com sucesso</StyledModalTitle>

            <StyledImportButton
                style={{ width: 126, marginTop: 24 }}
                onClick={handleClose}
            >
                ok
            </StyledImportButton>
        </Dialog>
    );

    const dialogError = Boolean(errorTitle) && (
        <DialogMessage
            type="error"
            show={Boolean(errorTitle)}
            title={errorTitle}
            text={errorMessage}
            onClose={() => {
                setErrorTitle("");
                setErrorMessage("");
            }}
        />
    );

    function handleCurrentPaginate(page: string) {
        setCurrentPage(Number(page));
    }

    function handleNextPaginate(page: string) {
        setCurrentPage(Number(page));
    }

    const pagination = totalRecords > itensPerPage && (
        <div
            style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                height: "74px",
                borderWidth: "0 1px 1px 1px",
                borderColor: "#E4E4E4",
                borderStyle: "solid",
            }}
        >
            <StyledLabel>Itens por página: {itensPerPage}</StyledLabel>
            <StyledLabel>
                {currentPage * itensPerPage + 1}-{"  "}
                {(currentPage + 1) * itensPerPage} de {totalRecords}
            </StyledLabel>
            <LeftArrow
                style={{
                    cursor: currentPage === 0 ? "default" : "pointer",
                    fill: currentPage === 0 ? "#C4A0FF" : "#9654FF",
                    marginRight: "12px",
                }}
                onClick={() => handleCurrentPaginate(String(currentPage - 1))}
            />
            <RightArrow
                style={{
                    cursor:
                        (currentPage + 1) * itensPerPage > totalRecords
                            ? "default"
                            : "pointer",
                    fill:
                        (currentPage + 1) * itensPerPage > totalRecords
                            ? "#C4A0FF"
                            : "#9654FF",
                    marginLeft: "12px",
                    marginRight: "16px",
                }}
                onClick={() => handleNextPaginate(String(currentPage + 1))}
            />
        </div>
    );

    const DialogEditAbonoForm = modalEdit && (
        <DialogEditAbono
            createModal={modalEdit}
            onHide={handleClose}
            objectAbono={objectAbono}
            handleEditCreate={(nomeAbono: string, qtdAfastamento: number) => {
                handleSubmitEdit(nomeAbono, qtdAfastamento);
            }}
        />
    );

    const DialogCreateAbonoForm = modalCreate && (
        <DialogCreateAbono
            createModal={modalCreate}
            onHide={handleClose}
            handleSubmitCreate={(nomeAbono: string, qtdAfastamento: number) => {
                handleSubmitCreate(nomeAbono, qtdAfastamento);
            }}
        />
    );

    return (
        <ThemeProvider theme={theme}>
            <StyledHeader>
                <StyledTitle>Motivo de Abono</StyledTitle>
                <StyledSubTitle>
                    Aqui você pode criar e/ou editar abonos
                </StyledSubTitle>
            </StyledHeader>
            <StyledBody>
                <Row
                    className="mt-5"
                    style={{ alignItems: "center", marginBottom: 25 }}
                >
                    <Col>
                        <TableStyles.TitleTable>
                            Todos os abonos cadastrados
                        </TableStyles.TitleTable>
                    </Col>
                    <Col />
                    <ButtonStyles.BtnPrimary
                        style={{ width: 184 }}
                        onClick={() => setModalCreate(true)}
                    >
                        Criar Abono
                    </ButtonStyles.BtnPrimary>
                </Row>
                <Row>
                    <Col>
                        <TableStyles.TablePrimary
                            bordered
                            striped
                            responsive
                            style={{ marginBottom: 0 }}
                        >
                            <thead>
                                <tr>
                                    <th>Nome do Abono</th>
                                    <th>Status</th>
                                    <th>Criado/Editado por</th>
                                    <th>Última modificação</th>
                                    <th>Quantidade de dias</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {abono?.length === 0 ? (
                                    <tr key={uuid()} className="table-empty">
                                        <td colSpan={7}>Sem informações</td>
                                    </tr>
                                ) : (
                                    abono.map((item) => (
                                        <tr key={uuid()}>
                                            <td>{item.motivo}</td>
                                            <td>
                                                {item.ativo
                                                    ? "Ativo"
                                                    : "Inativo"}
                                            </td>
                                            <td>{item.ultimaEdicaoUsuario}</td>
                                            <td>
                                                {StringUtils.formatDateString(
                                                    item.ultimaEdicaoDataHora,
                                                    "dd/MM/yyyy - HH:mm"
                                                )}
                                            </td>
                                            <td>{item.quantidadeDias}</td>
                                            <td>
                                                {actionsTable(
                                                    item.ativo,
                                                    item.tipoAbonoID
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </TableStyles.TablePrimary>
                    </Col>
                </Row>
            </StyledBody>
            {pagination}
            {DialogCreateAbonoForm}
            {DialogEditAbonoForm}
            {DialogActiveConfirm}
            {DialogConfirm}
            {dialogError}
        </ThemeProvider>
    );
}

export default gestaoAbono;
